import { Header, Loader, Modal } from '@/components';
import { Button, BookingContext } from '@sirkka-health/booking-system-ui';
import { useContext, useEffect, useState } from 'react';
import Image from '@/components/Image';
import { useNavigate, useParams } from 'react-router-dom';
import { buildGeneralCartObject } from '@/helpers/addToCart';
import { slugify } from '@/helpers/slugifier';

const healthOwlServiceData = {
	entity_with_services: {
		booking_entity: {
			type: "['In-Clinic', 'Home Visit']",
			image_url: '/img/logo/health-owl.png',
			description:
				'The Health Owl Programme is a 12-week journey designed to transform your health and wellness through a balanced approach to movement, nutrition and mindset.',
			website_url: 'https://www.thehealthowl.org/',
			display_name: 'Health Owl',
			is_published: true,
			phone_number: '0330 088 2538',
		},
		available_services: [
			{
				service: {
					bookable: true,
					description:
						'An essential blood panel covering key markers for overall health, ideal for those wanting a broad overview',
					redirect_url: null,
					service_code: 'healthOwlProfile1',
					service_type: 'Home Visit',
					service_icon: 'health_screening',
					service_name: 'Core Wellness Pack',
					appt_duration: '15',
					service_price: 22500,
					appt_stackable: 'true',
					email_template: 'generalBloodTest',
					service_status: 'true',
					enable_discount: true,
					tdl_lab_code: ['SK94'],
				},
				locations: ['Home Visit'],
			},
			{
				service: {
					bookable: true,
					description:
						'A deeper dive into your wellbeing with a wider range of indicators for long-term health and fitness',
					redirect_url: null,
					service_code: 'healthOwlProfile2',
					service_type: 'Home Visit',
					service_icon: 'health_screening',
					service_name: 'Advanced Health Profile',
					appt_duration: '15',
					service_price: 28500,
					appt_stackable: 'true',
					email_template: 'generalBloodTest',
					service_status: 'true',
					enable_discount: true,
					tdl_lab_code: ['SK95'],
				},
				locations: ['Home Visit'],
			},
			{
				service: {
					bookable: true,
					description:
						'At-home test that offers insights into conditions like PCOS, early menopause and hormonal balance',
					redirect_url: null,
					service_code: 'healthOwlProfile3',
					service_type: 'Home Visit',
					service_icon: 'female_hormone_screening',
					service_name: 'Female Hormone Screening',
					appt_duration: '15',
					service_price: 9800,
					appt_stackable: 'true',
					email_template: 'generalBloodTest',
					service_status: 'true',
					enable_discount: true,
					tdl_lab_code: ['SK90'],
				},
				locations: ['Postal'],
			},
			{
				service: {
					bookable: true,
					description:
						'Tailored for midlife women to track key indicators around menopause, heart, and thyroid health',
					redirect_url: null,
					service_code: 'healthOwlProfile4',
					service_type: 'Home Visit',
					service_icon: 'health_screening',
					service_name: 'Menopause & Heart Health',
					appt_duration: '15',
					service_price: 14000,
					appt_stackable: 'true',
					email_template: 'generalBloodTest',
					service_status: 'true',
					enable_discount: true,
					tdl_lab_code: ['FBC', 'TSH', 'LIPP'],
				},
				locations: ['Home Visit'],
			},
			{
				service: {
					bookable: true,
					description:
						'Understand your testosterone levels and broader hormonal health. Ideal for those experiencing fatigue, low mood, or performance changes.',
					redirect_url: null,
					service_code: 'healthOwlProfile5',
					service_type: 'Home Visit',
					service_icon: 'male_hormone_screening',
					service_name: 'Male Hormone Profile',
					appt_duration: '15',
					service_price: 19800,
					appt_stackable: 'true',
					email_template: 'generalBloodTest',
					service_status: 'true',
					enable_discount: true,
					tdl_lab_code: ['MIPR'],
				},
				locations: ['Home Visit'],
			},
		],
	},
};

type EntityWithServices = typeof healthOwlServiceData;

const HealthOwlBookingPage = () => {
	const { serviceSlug } = useParams();
	const navigate = useNavigate();
	const { setBookingData } = useContext<any>(BookingContext);
	const [loading, setLoading] = useState(true);
	const [entityItem, setEntityItem] = useState<EntityWithServices | null>(null);
	const [showAllServices, setShowAllServices] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [modalService, setModalService] = useState<any | null>(null);

	const title = 'HealthOwl Customer Booking';

	useEffect(() => {
		setEntityItem(healthOwlServiceData);
		setLoading(false);
	}, []);

	useEffect(() => {
		if (entityItem && serviceSlug) {
			const target = entityItem.entity_with_services.available_services.find(
				(s) => slugify(s.service.service_name) === serviceSlug
			);
			if (target) setModalService(target);
		}
	}, [entityItem, serviceSlug]);

	const toggleServices = () => setShowAllServices(!showAllServices);

	const handleBook = (service: any, type: string) => {
		if (!entityItem) return;
		if (type.toLowerCase().includes('home visit')) {
			navigate('/booking/home-visit', {
				state: {
					selectedServiceItem: { ...service, service_type: 'Home Visit' },
					bookingEntityId: 1,
					noAdditionalCost: true,
				},
			});
		} else {
			// Postal
			const location = { building_name: '', street: '', city: '', postcode: '' };
			const selectedServiceItem = { ...service, service_type: 'Postal' };
			const selectedEntityItem = {
				type: "['In-Clinic', 'Home Visit']",
				email: 'info@sirkkaltd.com',
				display_name: 'Sirkka Health',
				is_published: true,
				phone_number: '0330 088 2538',
				internal_notes: null,
				booking_entity_id: 1,
			};
			const processed = buildGeneralCartObject(location, selectedServiceItem, selectedEntityItem);
			setBookingData([processed]);
			navigate('/cart');
		}
	};

	if (loading) return <Loader />;

	if (!entityItem) {
		return (
			<>
				<Header title={title} fullTitle={title} />
				<div className="container py-4">
					<p>No booking entity found.</p>
				</div>
			</>
		);
	}

	const bookingEntity = entityItem.entity_with_services.booking_entity;
	const availableServices = entityItem.entity_with_services.available_services;

	const cardStyle = {
		backgroundColor: 'rgb(255, 255, 255)',
		padding: 0,
		paddingBottom: '18px',
		borderRadius: '8px',
		border: '1px solid rgba(203, 205, 215, 0.231)',
		boxShadow: isHovered ? '0px 2px 4px rgba(0, 0, 0, 0.2)' : 'none',
	};

	return (
		<>
			<Header title={title} fullTitle={title} />
			<div className="container py-4">
				<div
					className="px-3 py-3 mb-3"
					style={cardStyle}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<div className="d-flex flex-column flex-md-row card-content-container">
						<div className="col-12 col-md-5">
							<div className="image-container">
								<Image src={bookingEntity.image_url} alt={bookingEntity.display_name} />
							</div>
						</div>
						<div className="col-12 col-md-7 ps-md-3 pt-lg-5 pt-md-2">
							<div className="d-flex align-items-center mt-md-5 mb-15">
								<div className="col-9">
									<h2 className="mb-0">{bookingEntity.display_name}</h2>
								</div>
								<div className="col-3"></div>
							</div>
							<h5 className="col-9 mt-md-2 mb-md-3">{bookingEntity.description}</h5>
							<div className="col-3">
								{bookingEntity.website_url && (
									<a href={bookingEntity.website_url} target="_blank" rel="noopener noreferrer">
										<Button
											label={bookingEntity.website_url.startsWith('mailto') ? 'Email' : 'Website'}
											fullWidth
											variant="primary"
										/>
									</a>
								)}
							</div>
						</div>
					</div>

					<div className="category-section mt-4">
						<h4 style={{ color: 'navy' }}>Available Services</h4>
						{availableServices
							.slice(0, showAllServices ? availableServices.length : 3)
							.map((serviceItem, index) => (
								<div className="card-service d-flex px-3 mt-3" key={index}>
									<div className="col-9 col-lg-10">
										<h6 style={{ fontSize: '16px' }}>{serviceItem.service.service_name}</h6>
										<p>{serviceItem.service.description}</p>
									</div>
									<div className="col-3 col-lg-2 text-end align-self-center">
										<div className="d-flex gap-2 justify-content-end flex-wrap">
											<Button
												label="Book"
												onClick={() => setModalService(serviceItem)}
												variant="primary"
											/>
										</div>
									</div>
								</div>
							))}
					</div>

					{availableServices.length > 3 && (
						<button
							className={`view-more-button pb-2 ${showAllServices ? 'expanded' : ''}`}
							onClick={toggleServices}
						>
							{showAllServices ? 'View Less' : 'View all'}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<polyline points="6 9 12 15 18 9"></polyline>
							</svg>
						</button>
					)}
				</div>
			</div>
			<Modal
				isOpen={!!modalService}
				onClose={() => {
					setModalService(null);
					navigate('/health-owl');
				}}
				content={
					modalService && (
						<div>
							<Image
								src={`/img/icon/${modalService.service.service_icon || 'default'}.png`}
								alt={modalService.service.service_name}
								style={{
									borderRadius: '50%',
									margin: 'auto',
									overflow: 'hidden',
									height: '120px',
									display: 'block',
								}}
							/>

							<h1 className="text-center mt-3">{modalService.service.service_name}</h1>
							<h6
								className="text-center"
								style={{
									marginTop: '12px',
									color: '#0279a3',
									fontWeight: '500',
									fontSize: '18px',
								}}
							>
								{modalService.service.service_price > 0 &&
									(modalService.service.service_price / 100).toLocaleString('en-GB', {
										style: 'currency',
										currency: 'GBP',
									})}
							</h6>

							{modalService.service.description && (
								<p className="text-center px-3 mt-2" style={{ fontSize: '14px', color: '#333' }}>
									{modalService.service.description}
								</p>
							)}

							<div className="mt-4">
								<h5>Available Locations:</h5>
								{modalService.locations.length > 0 ? (
									modalService.locations.map((locationType: string, index: number) => (
										<div
											className="d-flex align-items-center"
											key={`modal-location-${index}`}
											style={{
												backgroundColor: index % 2 === 0 ? 'transparent' : '#f2f2f2',
												padding: '12px',
											}}
										>
											<div className="col-6">
												<h6 className="m-0">
													{locationType === 'Postal' ? 'By post' : locationType}
												</h6>
												<p className="m-0">
													{locationType === 'Postal'
														? 'Delivered to you to take at home.'
														: 'A healthcare professional will visit your address.'}
												</p>
											</div>

											<div className="col-6 justify-content-end d-flex">
												<Button
													label={`Select ${locationType}`}
													disabled={false}
													variant="border"
													onClick={() => {
														handleBook(modalService.service, locationType);
														setModalService(null);
													}}
												/>
											</div>
										</div>
									))
								) : (
									<p>No locations available for this service.</p>
								)}
							</div>

							<div className="d-flex justify-content-center mt-4">
								<Button
									label="Cancel"
									onClick={() => {
										setModalService(null);
										navigate('/health-owl');
									}}
									variant="border"
								/>
							</div>
						</div>
					)
				}
			/>
		</>
	);
};

export default HealthOwlBookingPage;
